import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { DropdownMenu } from '@/shell/Menus/DropdownMenu';
import type { MenuItem } from '@/shell/Menus/types';
import { hasDefaultVerb } from './common';
import { asTextField, textFieldMenu } from './edit';
import { useContextMenuStore } from './store';

/** The attribute every `DropdownMenu` popup carries, used to find ours again. */
const POPUP_SELECTOR = '[data-xp-menupopup]';

/** Nudge a keyboard-raised menu clear of the element's own left edge. */
const KEY_INSET = 8;

/**
 * Where a menu raised by this event belongs.
 *
 * Shift+F10 and the Menu key raise `contextmenu` with no pointer behind them,
 * and the browser reports (0, 0) for those. Anchor on the element instead, so
 * the menu still appears next to whatever the keyboard is on.
 */
function anchorFor(event: { clientX: number; clientY: number }, on: Element | null): {
  x: number;
  y: number;
} {
  if (event.clientX !== 0 || event.clientY !== 0) return { x: event.clientX, y: event.clientY };

  const rect = on?.getBoundingClientRect();
  if (!rect) return { x: 0, y: 0 };
  return { x: Math.round(rect.left) + KEY_INSET, y: Math.round(rect.bottom) };
}

/**
 * Raise the shell's context menu for a `contextmenu` event.
 *
 * Always swallows the browser's own menu, and stops the event so a nested
 * target (a desktop icon, a task button) wins over the surface behind it.
 */
export function showContextMenu(event: ReactMouseEvent<Element>, items: MenuItem[]): void {
  event.preventDefault();
  event.stopPropagation();

  const { x, y } = anchorFor(event, event.currentTarget);
  useContextMenuStore.getState().show(x, y, items);
}

/**
 * Drop a menu below an element, for the controls that open one on an ordinary
 * click: a window's caption icon, and anything else that behaves like the
 * System menu button it is.
 */
export function showMenuUnder(el: Element, items: MenuItem[]): void {
  const rect = el.getBoundingClientRect();
  useContextMenuStore.getState().show(Math.round(rect.left), Math.round(rect.bottom), items);
}

/**
 * The shell's single context-menu layer. Mounted once, by `Desktop`.
 *
 * It owns no popup of its own: it drives the same `DropdownMenu` the
 * application menu bars use, which already portals out of every clipping
 * ancestor, flips at the viewport edge, keeps itself clear of the taskbar and
 * handles the whole keyboard contract including Escape.
 */
export function ContextMenu() {
  const open = useContextMenuStore((s) => s.open);
  const hide = useContextMenuStore((s) => s.hide);
  const restoreRef = useRef<HTMLElement | null>(null);

  /* ----------------------------------------------------------------- focus */

  // Remember what had the keyboard before the popup took it. This runs in the
  // capture phase, ahead of the React handler that opens the menu — by the time
  // the popup has mounted, focus has already moved into it.
  useEffect(() => {
    const remember = () => {
      const active = document.activeElement;
      restoreRef.current =
        active instanceof HTMLElement && active !== document.body ? active : null;
    };
    document.addEventListener('contextmenu', remember, true);
    return () => document.removeEventListener('contextmenu', remember, true);
  }, []);

  /* ---------------------------------------------------------- the default */

  /**
   * What happens when nothing else claimed the right-click.
   *
   * Two things, both of which the shell owes the illusion. Chrome's own menu
   * never appears over the desktop — one "Open image in new tab" and the whole
   * game is up — and a text box gets the menu every text box in Windows had,
   * without each dialog and address bar having to ask for it.
   *
   * This listens on the document, in the bubble phase, so it runs after every
   * React handler above it: a target that raised its own menu has already
   * called `preventDefault`, and we leave it alone.
   */
  useEffect(() => {
    const fallback = (event: MouseEvent) => {
      if (event.defaultPrevented) return;
      event.preventDefault();

      const field = asTextField(event.target);
      if (!field) return;

      const { x, y } = anchorFor(event, field);
      useContextMenuStore.getState().show(x, y, textFieldMenu(field));
    };

    document.addEventListener('contextmenu', fallback);
    return () => document.removeEventListener('contextmenu', fallback);
  }, []);

  const close = useCallback(() => {
    hide();

    const target = restoreRef.current;
    restoreRef.current = null;
    if (!target || !target.isConnected) return;

    // The popup is still mounted and still focused at this point — React has
    // not re-rendered yet — so "focus is in a popup" counts as nobody having
    // claimed it. Anything else did, and keeps it.
    const active = document.activeElement;
    const inPopup = active instanceof Element && active.closest(POPUP_SELECTOR) !== null;
    if (active && active !== document.body && !inPopup) return;

    target.focus({ preventScroll: true });
  }, [hide]);

  /* ---------------------------------------------------------- default verb */

  const bold = open !== null && hasDefaultVerb(open.items);

  // XP drew the default verb — the one a double-click would run — in bold.
  // `MenuItem` has no flag for it and `DropdownMenu` is shared with the menu
  // bars, so the emphasis is applied to our own popup here, once it is up.
  // Ours is the last popup on the page: opening a context menu dismisses every
  // other one first, and submenus only appear later.
  useLayoutEffect(() => {
    if (!bold) return;
    const popups = document.querySelectorAll<HTMLElement>(POPUP_SELECTOR);
    const row = popups[popups.length - 1]?.querySelector<HTMLElement>('button[role]');
    if (!row) return;
    row.style.fontWeight = '700';
    return () => {
      row.style.fontWeight = '';
    };
  }, [bold, open]);

  if (!open) return null;

  return (
    <DropdownMenu
      /* A menu raised somewhere else is a new popup, not the old one moved:
         highlight, open submenu and dismissal listeners all belong to the
         gesture that opened it. */
      key={`${open.x},${open.y},${open.items.length}`}
      items={open.items}
      x={open.x}
      y={open.y}
      onClose={close}
    />
  );
}
